
import React from 'react';
import { Box } from 'grommet';
import { Map, View } from 'ol';

import VectorLayer from 'ol/layer/Vector.js';
import VectorSource from 'ol/source/Vector.js';
import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';
import { Icon, Style } from 'ol/style.js';
import { defaults as defaultControls } from 'ol/control.js';
import { defaults as defaultInteractions, DragZoom } from 'ol/interaction';
import { platformModifierKeyOnly } from 'ol/events/condition';

import { createLayers, createProjection } from './olUtils';

import 'ol/ol.css';

const ICON = require('../../img/blue-pushpin.png');

class MapView extends React.Component {

    addPoints(points) {
        let source = this._vectorPoints.getSource()
        source.clear();
        points.forEach(item => {
            let feature = new Feature({
                geometry: new Point([item.x, item.y]),
                name: item.title
            });
            source.addFeature(feature);
        });

        if (source.getFeatures().length > 0) {
            let extent = source.getExtent();
            this._map.getView().fit(extent);
        }
    }

    createMap(map) {
        let view = new View({
            resolutions: map.definition.resolutions,
            projection: createProjection(map.definition.projection),
            center: this.getMapCenter(map.center),
            rotation: map.rotation,
            zoom: this.getMapZoom(map.zoom),
            minZoom: map.definition.minZoom,
            maxZoom: map.definition.maxZoom
        });

        view.on('propertychange',
            (e) => {
                switch (e.key) {
                    case 'center':
                        this.changeView(e.target.get(e.key), null, null);
                        break;
                    case 'resolution':
                        this.changeView(null, view.getZoomForResolution(e.target.get(e.key)), null);
                        break;
                    case 'rotation':
                        this.changeView(null, null, e.target.get(e.key));
                        break;
                    default:
                        break;
                }
            }
        );

        this._map = new Map({
            target: this.refs.mapContainer,
            controls: defaultControls(),
            interactions: this.getInteractions(),
            layers: [
                ...createLayers(map.definition.layers, view),
                this.createPointsLayer()
            ],
            view: view
        });

        this._map.on('click', (e) => {
            if (this.props.getInfo) {
                this.props.getInfo(
                    e.coordinate,
                    this.getVisibleLayers(),
                    this.getResolution()
                );
            }
        });

        this.changeLayerVisibility(this.props.map.layers);
        this.addPoints(this.props.map.points);
    }

    componentDidMount() {
        if (this.props.map.definition) {
            this.createMap(this.props.map);
        }
    }

    componentWillReceiveProps(nextProps) {
        if ((nextProps.map.definition !== this.props.map.definition) && (nextProps.map.definition)) {
            this.createMap(nextProps.map);
        }
        if (nextProps.map.layers !== this.props.map.layers) {
            this.changeLayerVisibility(nextProps.map.layers);
        }
        if (nextProps.map.points !== this.props.map.points) {
            this.addPoints(nextProps.map.points);
        }
    }

    createPointsLayer() {
        let style = new Style({
            image: new Icon({
                anchor: [10, 1.0],
                anchorXUnits: 'pixel',
                anchorYUnits: 'fraction',
                opacity: 1.0,
                src: ICON
            })
        });

        this._vectorPoints = new VectorLayer({
            source: new VectorSource(),
            style: style
        });
        return this._vectorPoints;
    }

    changeLayerVisibility(virtualLayers) {
        virtualLayers.forEach(virtualLayer => {
            virtualLayer.layers.forEach(item => {
                this._map.getLayers().forEach(layer => {
                    let layerId = layer.get('id');
                    if (layerId === item) {
                        layer.setVisible(virtualLayer.visible);
                    }
                });
            });
        });
    }

    changeView(center, zoom, rotation) {
        if (this.props.changeView) {
            this.props.changeView(center, zoom, rotation);
        }
    }

    getInteractions() {
        let interactions = defaultInteractions({
            shiftDragZoom : false
        });
        interactions.push(new DragZoom({
            condition: platformModifierKeyOnly
        }));
        return interactions;
    }

    getMapCenter(center) {
        if (this.props.queryParams && this.props.queryParams.coord) {
            try {
                const coords = this.props.queryParams.coord.split(',');
                center = [
                    parseInt(coords[0], 10),
                    parseInt(coords[1], 10)
                ];
            } catch (e) {
                // nop;
            }
        }
        return center;
    }

    getMapZoom(zoom) {
        if (this.props.queryParams && this.props.queryParams.zoom) {
            try {
                zoom = parseInt(this.props.queryParams.zoom, 10);
            } catch (e) {
                // nop;
            }
        }
        return zoom;
    }

    getResolution() {
        return this._map.getView().getResolution();
    }

    getVisibleLayers() {
        return this.props.map.layers.filter(item => {
            return (item.visible);
        }).map(item => {
            return item.id;
        });
    }

    render() {
        return (
            <Box ref="mapContainer" height="100%"></Box>
        );
    }
}

export default MapView;
